import {useMemo} from 'react';

import {addQueryToPath} from '../../../libs/shared/helpers/src/browser/urlHelpers';
import {
  ApiAutolinkersSearch,
  AutolinkerModel,
  AutolinkersSearchModel,
} from '../../models/api/autolinkerModel';
import {CompanyModel} from '../../models/api/companyModel';
import {PaginationParameter} from '../helpers/resourceListPagination/resourceListPaginationOffset';
import {makeResourceListPaginationSearchOffset} from '../helpers/resourceListPagination/resourceListPaginationSearchOffset';
import {usePersistedListMutation, useResourceList} from '../helpers/resourceTableHooks';
import {
  AutolinkerListMutation,
  autolinkerListMutations,
  AutolinkerListPageType,
} from './autolinkersMutations';
import {useQueryDebounce} from './fetcherHelpers';

/*
 * Consts.
 */

const autolinkerQueryKeyFactory = {
  all: ['autolinkers'],
  list: (query: string) => [...autolinkerQueryKeyFactory.all, {query}],
};

/*
 * Fetchers.
 */

export function useFetchAutolinkers(applicationId: number, query: string) {
  const debouncedQuery = useQueryDebounce(query);

  const queryKey = autolinkerQueryKeyFactory.list(debouncedQuery);

  const autolinkerPagination = useMemo(
    () =>
      makeResourceListPaginationSearchOffset<AutolinkerModel, AutolinkersSearchModel>({
        apiType: ApiAutolinkersSearch,
        makeSearchUrl: (page, company) =>
          buildAutolinkersSearchUrl(company, applicationId, debouncedQuery, page),
      }),
    [applicationId, debouncedQuery],
  );

  const {items, loadMore, moreStatus, hasError, showErrorBanner} = useResourceList(
    queryKey,
    autolinkerPagination,
  );

  const {mutate} = usePersistedListMutation<
    AutolinkerListMutation,
    AutolinkerListPageType,
    typeof autolinkerListMutations,
    Error
  >(autolinkerQueryKeyFactory.all, autolinkerListMutations, showErrorBanner, true);

  return {
    items,
    loadMore,
    moreStatus,
    hasError,
    mutate,
  };
}

/*
 * Helpers.
 */

function buildAutolinkersSearchUrl(
  company: CompanyModel,
  applicationId: number,
  query: string,
  pagination: PaginationParameter,
) {
  const path = `${company.url}/local_applications/${applicationId}/search_autolinkers/${encodeURIComponent(
    query.toLowerCase(),
  )}`;

  return addQueryToPath(path, pagination);
}
